import React from 'react';
import { Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { Reorder } from 'framer-motion';
import { useTheme } from '../../../../context/ThemeContext';
import {
  ActionIconButton,
  OptionCardDraggable,
  OptionInfo,
  OptionName as StyledOptionName,
  OptionPrice as StyledOptionPrice,
  OptionActions,
  SoldOutButton,
  OptionButtonsContainer,
} from '../Styles';
import { Option } from './types';

interface DraggableOptionProps {
  option: Option;
  value: number | string;
  handleEditOption: (option: Option, isSoldOut?: boolean) => void;
  handleOptionRemove: (optionId: number | string) => void;
  optionsBorderColor: string;
  soldOutLoading?: number | string | null;
}

export const DraggableOption = React.memo<DraggableOptionProps>(
  ({
    option,
    value,
    handleEditOption,
    handleOptionRemove,
    optionsBorderColor,
    soldOutLoading,
  }) => {
    const { isDarkMode } = useTheme();
    const colorMode = isDarkMode ? 'dark' : 'light';

    if (!option || !option.id) {
      console.warn('DraggableOption: Invalid option object', option);
      return null;
    }

    return (
      <Reorder.Item
        value={value}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 1.01 }}
        style={{ 
          cursor: 'grab',
          listStyle: 'none',
          marginBottom: '12px',
        }}
      >
      <OptionCardDraggable
        $isDark={colorMode === 'dark'}
        $isDraggable={true}
        style={{ cursor: 'grab' }}
      >
        <OptionInfo>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon 
              icon={faGripVertical} 
              fontSize={16} 
              color={colorMode === 'dark' ? '#9ca3af' : '#6b7280'}
              style={{ cursor: 'grab', marginRight: '4px' }}
            />
            <StyledOptionName strong>{option.option}</StyledOptionName>
          </div>
          <StyledOptionPrice type="secondary">
            {option.price !== undefined && !isNaN(option.price)
              ? `$${(option.price || 0).toFixed(2)}`
              : '$0.00'}
          </StyledOptionPrice>
        </OptionInfo>
        <OptionActions>
          <SoldOutButton
            size="small"
            onClick={() => handleEditOption(option, true)}
            loading={soldOutLoading === option.id}
            icon={soldOutLoading === option.id ? <Spin size="small" /> : null}
          >
            {option.sold_out ? 'Set In Stock' : 'Set Sold Out'}
          </SoldOutButton>
          <OptionButtonsContainer>
            <ActionIconButton
              iconColor="#2D6CCA"
              onClick={() => option && handleEditOption(option)}
            >
              <FontAwesomeIcon icon={faPen} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
            </ActionIconButton>
            <ActionIconButton
              iconColor="#f5222d"
              onClick={() => option?.id && handleOptionRemove(option.id)}
            >
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
            </ActionIconButton>
          </OptionButtonsContainer>
        </OptionActions>
      </OptionCardDraggable>
      </Reorder.Item>
    );
  }
);

DraggableOption.displayName = 'DraggableOption';

